import React, { useCallback, useEffect } from 'react';

import { Controller, useForm, useWatch } from 'react-hook-form';
import dayjs from '~/timeInit';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material';

import { DateTimeInput, Input, Select } from '~/components/Common/Forms/fields';

import { useCreatePointsPurchaseOfferCampaign } from '~/hooks/apis/loyalty/usePointsPurchaseMutations';

import { POINTS_PURCHASE_CAMPAIGN_TYPES, type PointsPurchaseOffer } from '~/types/loyaltyContract';

import getAllCurrencyCodes from '~/utils/getAllCurrencyCodes';

import { formatCampaignType } from './utils';

type CampaignScope = 'OFFER' | 'CURRENCY' | 'GLOBAL';

interface CreateCampaignDialogProps {
  open: boolean;
  onClose: () => void;
  offers: Array<PointsPurchaseOffer>;
}

interface CreateCampaignFormData {
  scope: CampaignScope;
  offerId: string;
  currency: string;
  type: 'PRICE_DISCOUNT' | 'BONUS_POINTS';
  value: string;
  startDate: string;
  endDate: string;
}

function buildDefaultValues(): CreateCampaignFormData {
  return {
    scope: 'GLOBAL',
    offerId: '',
    currency: '',
    type: 'BONUS_POINTS',
    value: '',
    startDate: dayjs().format('YYYY-MM-DDTHH:mm'),
    endDate: '',
  };
}

export default function CreateCampaignDialog({ open, onClose, offers }: CreateCampaignDialogProps) {
  const createMutation = useCreatePointsPurchaseOfferCampaign();

  const { control, handleSubmit, reset, getValues } = useForm<CreateCampaignFormData>({
    defaultValues: buildDefaultValues(),
    mode: 'onBlur',
  });

  const scope = useWatch({ control, name: 'scope' });

  useEffect(() => {
    if (!open) {
      reset(buildDefaultValues());
    }
  }, [open, reset]);

  const validateEndDate = (value: string) => {
    if (!value) {
      return true;
    }
    const startDate = getValues('startDate');
    if (startDate && dayjs(value).isBefore(dayjs(startDate))) {
      return 'End date must be after start date';
    }
    return true;
  };

  const onSubmit = useCallback(
    async (data: CreateCampaignFormData) => {
      try {
        await createMutation.mutateAsync({
          type: data.type,
          value: Number(data.value),
          offerId: data.scope === 'OFFER' ? data.offerId : undefined,
          currency: data.scope === 'CURRENCY' ? data.currency : undefined,
          startDate: dayjs(data.startDate).toISOString(),
          endDate: data.endDate ? dayjs(data.endDate).toISOString() : undefined,
        });
        onClose();
      } catch {
        // Error handled in mutation hook
      }
    },
    [createMutation, onClose],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Campaign</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Controller
            name="scope"
            control={control}
            render={({ field }) => (
              <FormControl>
                <FormLabel>Scope</FormLabel>
                <RadioGroup row value={field.value} onChange={field.onChange}>
                  <FormControlLabel value="OFFER" control={<Radio />} label="Offer-specific" />
                  <FormControlLabel value="CURRENCY" control={<Radio />} label="Currency-specific" />
                  <FormControlLabel value="GLOBAL" control={<Radio />} label="Global" />
                </RadioGroup>
              </FormControl>
            )}
          />

          {scope === 'OFFER' && (
            <Select
              control={control}
              name="offerId"
              muiLabel="Offer"
              rules={{ required: 'Offer is required for offer-specific scope' }}
            >
              {offers.map((offer) => (
                <MenuItem key={offer.id} value={offer.id}>
                  {offer.title} ({offer.basePoints.toLocaleString()} pts — {offer.currency})
                </MenuItem>
              ))}
            </Select>
          )}

          {scope === 'CURRENCY' && (
            <Select
              control={control}
              name="currency"
              muiLabel="Target Currency"
              rules={{ required: 'Currency is required for currency-specific scope' }}
            >
              {getAllCurrencyCodes().map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          )}

          <Select control={control} name="type" muiLabel="Type" rules={{ required: 'Type is required' }}>
            {POINTS_PURCHASE_CAMPAIGN_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {formatCampaignType(t)}
              </MenuItem>
            ))}
          </Select>

          <Input
            control={control}
            name="value"
            muiLabel="Value"
            type="number"
            rules={{
              required: 'Value is required',
              validate: (v: string) => {
                const n = Number(v);
                if (!Number.isInteger(n)) return 'Must be a whole number';
                if (n <= 0) return 'Must be greater than 0';
                if (n > 100) return 'Must be at most 100';
                return true;
              },
            }}
            fullWidth
            required
            helperText="For PRICE_DISCOUNT: percentage off price. For BONUS_POINTS: percentage bonus on top of base points."
          />

          <DateTimeInput
            control={control}
            name="startDate"
            label="Start Date"
            rules={{ required: 'Start date is required' }}
            fullWidth
            required
          />
          <DateTimeInput
            control={control}
            name="endDate"
            label="End Date"
            rules={{ validate: validateEndDate, deps: ['startDate'] }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={createMutation.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={createMutation.isPending}>
          {createMutation.isPending ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
